<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import { AtomText, AtomButton } from "@chatfood/design-system";
import { computed } from "vue";
import type { IChangeOrderingModeProps } from "./props";
import { useLocale } from "~/composables/use-locale";
import { useBasketStore } from "~/stores/basket";
import { useNavigation } from "~/composables/use-navigation";
import { useDomainLabel } from "~/composables/use-domain-label";
import type { IDomainOrderingMode } from "#imports";

const basket = useBasketStore();
const props = defineProps<IChangeOrderingModeProps>();

const emits = defineEmits<{
  close: [MouseEvent];
  change: [MouseEvent];
}>();
const css = bemBuilder("change-order-mode");
const { t } = useLocale();
const { push } = useNavigation();
const { orderingModeLabel } = useDomainLabel();

const targetMode = computed<IDomainOrderingMode>(() => {
  return basket.orderingMode === "delivery" ? "pickup" : "delivery";
});

const redefineOrderingMode = (event: MouseEvent) => {
  push("order", {
    orderingMode: basket.orderingMode === "delivery" ? "pickup" : "delivery",
  });
  emits("change", event);
};
</script>

<template>
  <div :class="css()">
    <AtomText type="h3" color="color-text-headline">
      {{
        props.title
          ? props.title
          : t("components.change_ordering_mode.title", {
              orderMode: orderingModeLabel(targetMode),
            })
      }}
    </AtomText>
    <div :class="css('text')">
      <AtomText color="color-base-0" type="body">
        {{
          props.text
            ? props.text
            : t("components.change_ordering_mode.text", {
                orderMode: orderingModeLabel(targetMode),
              })
        }}
      </AtomText>
      <AtomText color="color-base-0" type="small">
        {{ t("components.change_ordering_mode.sub_text") }}
      </AtomText>
    </div>
    <div :class="css('ctas')">
      <AtomButton
        theme="ghost"
        wide
        data-test="close-cta"
        @click="emits('close', $event)"
      >
        {{ t("components.change_ordering_mode.cancel_cta") }}
      </AtomButton>
      <AtomButton
        theme="filled"
        data-test="change-mode-cta"
        wide
        @click="redefineOrderingMode"
      >
        {{ orderingModeLabel(targetMode) }}
      </AtomButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.change-order-mode {
  padding: var(--spacing-lg);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);

  &__text {
    display: flex;
    gap: var(--spacing-sm);
    flex-direction: column;
  }

  &__ctas {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--spacing-sm);
  }
}
</style>
